/**
 * Pure Design State Computation
 *
 * Computes RelativeDesignState from serialized canvas JSON + PrintContext,
 * WITHOUT requiring a Fabric.js canvas instance. This enables deterministic
 * preview computation for ALL positions immediately on load.
 *
 * Equivalent to extractDesignAdaptationData() in canvasStateManager.ts,
 * but using pure 2D affine matrix math instead of Fabric.js getBoundingRect().
 *
 * Key Features:
 * - Framework-independent (no Fabric.js, no React, no DOM)
 * - Handles rotation, scale, groups, and activeSelection
 * - Corner-transform AABB for accurate bounding boxes
 *
 * Known Limitations (documented, not blocking):
 * - skewX/skewY: Ignored (not standard in PoD editors)
 * - strokeUniform: Not supported (rare edge case)
 */

import type { RelativeDesignState } from './placementTypes.js';

// =============================================================================
// SYSTEM LAYER NAMES (Single Source of Truth)
// =============================================================================

/**
 * Named system layer constants for layer creation and identification.
 * Used by systemLayerManager.ts for creating system layers.
 *
 * @example
 * import { SYSTEM_LAYER_NAMES } from '@designdeck/shared';
 * layer.set({ name: SYSTEM_LAYER_NAMES.BACKGROUND });
 */
export const SYSTEM_LAYER_NAMES = {
  BACKGROUND: '__background__',
  MOCKUP: '__mockup__',
  PRINT_AREA_BORDER: '__printAreaBorder__',
} as const;

export type SystemLayerName =
  (typeof SYSTEM_LAYER_NAMES)[keyof typeof SYSTEM_LAYER_NAMES];

/**
 * Set of all system layer name values for fast lookup (.has()).
 * Used by computeRelativeDesignStateFromJSON() and computeShortHash()
 * to filter out non-user objects.
 *
 * NOTE: Design decision — Exact vs. Heuristic filtering:
 * - This Set uses EXACT matching (obj.name must be in the set).
 * - The frontend's isSystemLayer() in systemLayerManager.ts uses HEURISTIC
 *   matching (name.startsWith('__')), which is broader and catches future
 *   system layers automatically.
 * - For pure (JSON-only) computation, exact matching is preferred:
 *   it avoids false positives for user objects that might accidentally
 *   use '__' prefix, and it's deterministic across versions.
 * - If new system layers are added, update SYSTEM_LAYER_NAMES above.
 *
 * @example
 * import { SYSTEM_LAYER_NAME_SET } from '@designdeck/shared';
 * const isSystem = SYSTEM_LAYER_NAME_SET.has(obj.name);
 */
export const SYSTEM_LAYER_NAME_SET: ReadonlySet<string> = new Set(
  Object.values(SYSTEM_LAYER_NAMES)
);

// =============================================================================
// TYPES
// =============================================================================

/**
 * Minimal representation of a Fabric.js serialized object.
 * Only the fields needed for bounding-box computation.
 */
export interface CanvasObjectJSON {
  type?: string;
  name?: string;
  left?: number;
  top?: number;
  width?: number;
  height?: number;
  scaleX?: number;
  scaleY?: number;
  angle?: number;
  originX?: 'left' | 'center' | 'right' | string;
  originY?: 'top' | 'center' | 'bottom' | string;
  strokeWidth?: number;
  /** Nested objects (Groups and activeSelection) */
  objects?: CanvasObjectJSON[];
}

/** Axis-aligned bounding box */
export interface BoundingBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

/** PrintContext subset needed for normalization */
export interface PrintContextForComputation {
  widthPx: number;
  heightPx: number;
  offsetX: number;
  offsetY: number;
}

// =============================================================================
// 2D AFFINE MATRIX MATH
// =============================================================================

/**
 * 2D affine transform matrix [a, b, c, d, tx, ty]
 * Represents: | a  c  tx |
 *             | b  d  ty |
 *             | 0  0  1  |
 */
type AffineMatrix = [number, number, number, number, number, number];

/** Identity matrix */
const IDENTITY: AffineMatrix = [1, 0, 0, 1, 0, 0];

/** Multiply two affine matrices: result = m1 * m2 */
function multiplyMatrices(m1: AffineMatrix, m2: AffineMatrix): AffineMatrix {
  return [
    m1[0] * m2[0] + m1[2] * m2[1],
    m1[1] * m2[0] + m1[3] * m2[1],
    m1[0] * m2[2] + m1[2] * m2[3],
    m1[1] * m2[2] + m1[3] * m2[3],
    m1[0] * m2[4] + m1[2] * m2[5] + m1[4],
    m1[1] * m2[4] + m1[3] * m2[5] + m1[5],
  ];
}

/** Transform a point through an affine matrix */
function transformPoint(
  m: AffineMatrix,
  x: number,
  y: number
): [number, number] {
  return [m[0] * x + m[2] * y + m[4], m[1] * x + m[3] * y + m[5]];
}

/**
 * Build an affine matrix from Fabric.js object properties.
 * Encodes: Translate(left, top) * Rotate(angle) * Scale(scaleX, scaleY)
 *
 * IMPORTANT: width/height are NOT included here -- they define the LOCAL shape size.
 * Scale is part of the matrix, so local corners use UNSCALED w/h.
 */
function buildObjectMatrix(obj: CanvasObjectJSON): AffineMatrix {
  const angle = ((obj.angle ?? 0) * Math.PI) / 180;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const sx = obj.scaleX ?? 1;
  const sy = obj.scaleY ?? 1;
  const tx = obj.left ?? 0;
  const ty = obj.top ?? 0;
  // Matrix = T * R * S
  return [sx * cos, sx * sin, -sy * sin, sy * cos, tx, ty];
}

// =============================================================================
// BOUNDING BOX COMPUTATION
// =============================================================================

/**
 * Compute the AABB (axis-aligned bounding box) of a Fabric.js object by
 * transforming all 4 corners through the world matrix, then taking min/max.
 *
 * UNIFIED CODEPATH: works identically for leaf objects and groups.
 * No separate trigonometric rotW/rotH formula -- the matrix handles everything.
 *
 * For Groups: builds worldMatrix = parent * group, then recurses into children.
 * For Leafs: computes 4 local corners (accounting for origin + strokeWidth),
 *            transforms them through worldMatrix, then min/max.
 */
export function computeObjectBoundingBox(
  obj: CanvasObjectJSON,
  parentMatrix: AffineMatrix = IDENTITY
): BoundingBox {
  const localMatrix = buildObjectMatrix(obj);
  const worldMatrix = multiplyMatrices(parentMatrix, localMatrix);

  // Handle containers (Groups AND activeSelection): recurse into children.
  // Fabric.js may serialize multi-select as 'activeSelection' instead of 'group'.
  // Check obj.objects?.length rather than obj.type === 'group' for robustness.
  if (obj.objects?.length) {
    let minX = Infinity,
      minY = Infinity;
    let maxX = -Infinity,
      maxY = -Infinity;

    for (const child of obj.objects) {
      const childBox = computeObjectBoundingBox(child, worldMatrix);
      minX = Math.min(minX, childBox.left);
      minY = Math.min(minY, childBox.top);
      maxX = Math.max(maxX, childBox.left + childBox.width);
      maxY = Math.max(maxY, childBox.top + childBox.height);
    }

    return {
      left: minX,
      top: minY,
      width: maxX - minX,
      height: maxY - minY,
    };
  }

  // Leaf object: compute 4 LOCAL corners relative to origin, then transform
  const stroke = obj.strokeWidth ?? 0;
  const w = (obj.width ?? 0) + stroke; // UNSCALED -- matrix handles scale
  const h = (obj.height ?? 0) + stroke; // UNSCALED -- matrix handles scale

  // Determine origin offset (Fabric.js v6 default is 'center')
  const originX = obj.originX ?? 'center';
  const originY = obj.originY ?? 'center';
  const ox = originX === 'center' ? -w / 2 : originX === 'right' ? -w : 0;
  const oy = originY === 'center' ? -h / 2 : originY === 'bottom' ? -h : 0;

  // 4 local corners (unscaled, relative to origin)
  const corners: [number, number][] = [
    [ox, oy], // top-left
    [ox + w, oy], // top-right
    [ox + w, oy + h], // bottom-right
    [ox, oy + h], // bottom-left
  ];

  // Transform all 4 corners to world space, compute AABB
  let minX = Infinity,
    minY = Infinity;
  let maxX = -Infinity,
    maxY = -Infinity;

  for (const [lx, ly] of corners) {
    const [wx, wy] = transformPoint(worldMatrix, lx, ly);
    minX = Math.min(minX, wx);
    minY = Math.min(minY, wy);
    maxX = Math.max(maxX, wx);
    maxY = Math.max(maxY, wy);
  }

  return { left: minX, top: minY, width: maxX - minX, height: maxY - minY };
}

// =============================================================================
// MAIN FUNCTION
// =============================================================================

/**
 * Compute RelativeDesignState from serialized canvas JSON + PrintContext.
 * Equivalent to extractDesignAdaptationData() but without Fabric.js canvas.
 *
 * @param canvasJSON - Serialized Fabric.js canvas (from DB)
 * @param printContext - PrintArea dimensions (from DB or computed)
 * @returns RelativeDesignState (0-1 normalized) or null if no user objects
 */
export function computeRelativeDesignStateFromJSON(
  canvasJSON: { objects?: CanvasObjectJSON[] },
  printContext: PrintContextForComputation
): RelativeDesignState | null {
  // 1. Filter system layers (same logic as isSystemLayer in systemLayerManager.ts)
  const userObjects = (canvasJSON.objects ?? []).filter(
    (obj) => !SYSTEM_LAYER_NAME_SET.has(obj.name ?? '')
  );

  if (userObjects.length === 0) return null;

  // 2. Compute combined bounding box (pure math, handles rotation + scale)
  let minLeft = Infinity,
    minTop = Infinity;
  let maxRight = -Infinity,
    maxBottom = -Infinity;

  for (const obj of userObjects) {
    const bbox = computeObjectBoundingBox(obj);
    minLeft = Math.min(minLeft, bbox.left);
    minTop = Math.min(minTop, bbox.top);
    maxRight = Math.max(maxRight, bbox.left + bbox.width);
    maxBottom = Math.max(maxBottom, bbox.top + bbox.height);
  }

  const bboxW = maxRight - minLeft;
  const bboxH = maxBottom - minTop;
  if (bboxW <= 0 || bboxH <= 0) return null;

  // 3. Normalize to 0-1 coordinates (identical formula to extractDesignAdaptationData)
  return {
    relCenterX:
      (minLeft - printContext.offsetX + bboxW / 2) / printContext.widthPx,
    relCenterY:
      (minTop - printContext.offsetY + bboxH / 2) / printContext.heightPx,
    relWidth: bboxW / printContext.widthPx,
    designRatio: bboxH / bboxW,
  };
}
